import { ExternalLink } from 'lucide-react';
import { useBranding, useFooterNav, useSiteLayout } from '../../contexts/SiteContext';
import { useViewTransition } from '../../hooks/useViewTransition';
import type { CmsNavItem } from '../../types/cms';

function FooterLink({ item }: { item: CmsNavItem }) {
  const navigate = useViewTransition();

  if (item.isExternal) {
    const safeHref =
      item.path.startsWith('http://') || item.path.startsWith('https://') ? item.path : '#';
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
      >
        {item.label}
        <ExternalLink size={11} />
      </a>
    );
  }

  return (
    <button
      onClick={() => navigate(item.path)}
      className="text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
    >
      {item.label}
    </button>
  );
}

export function SiteFooter() {
  const layout = useSiteLayout();
  const branding = useBranding();
  const footerItems = useFooterNav();

  if (layout.footerStyle === 'none') return null;

  if (layout.footerStyle === 'minimal') {
    return (
      <footer className="border-t border-zinc-100 py-4 text-center text-xs text-zinc-400">
        {branding.name} &copy; {new Date().getFullYear()}
      </footer>
    );
  }

  // full footer
  return (
    <footer className="border-t border-zinc-100 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          {/* Brand */}
          <div className="flex items-center gap-2.5">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={branding.name}
                className="h-6 w-auto object-contain"
              />
            ) : (
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {branding.name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-sm font-semibold text-zinc-700">{branding.name}</span>
          </div>

          {/* Nav links */}
          {footerItems.length > 0 && (
            <nav className="flex flex-wrap gap-x-6 gap-y-2">
              {footerItems.map((item) => (
                <FooterLink key={item.id} item={item} />
              ))}
            </nav>
          )}

          {/* Copyright */}
          <p className="text-xs text-zinc-400">
            &copy; {new Date().getFullYear()} {branding.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
